import {
  forEachTriangleEdge,
  getCentroid,
  getPointsOfTriangle
} from "./delaunay"
import { default as pointInPolygon } from "point-in-polygon"
import { resetDownloadButton, updateDownloadButton } from "./download"

import { Curve, LineSegment, Point, Shape } from "./types"

import Delaunator from "delaunator"
import {
  cloneDeep,
  each,
  find,
  findIndex,
  first,
  flatMap,
  last,
  random
} from "lodash"

import {
  ALLOW_WALL_ISLANDS,
  MIN_DISTANCE,
  NUMBER_OF_POINTS,
  SHOULD_DRAW_EXTRA_INFO,
  SHOULD_SMOOTH,
  WALL_PERCENT
} from "./form"
const MAX_ALLOWABLE_FAILED_POINT_DRAWINGS = 200
const NUMBER_OF_EDGE_POINTS = 20 // multiple of 4: larger number does more to prevent weird line shapes along walls
const POINT_RADIUS = 4
const SVG_WIDTH = 630
const SVG_HEIGHT = 500
const PATH_COLOR = "white"
const WALL_COLOR = "black"
let failedPointDrawings = 0
let lineSegments: LineSegment[] = []
let points: Point[] = []
let shapes: Shape[] = []
let shapeTypes: string[] = []
let svgElements: string = ""
let delaunay: Delaunator<unknown> | undefined

const displayError = () => {
  document.getElementById("svg")!.innerHTML =
    '<text fill="red" y="16" x="8">An error occurred. See the console for more info.</text>'
}

const checkIfDrawable = (x: number, y: number): boolean => {
  // too close to top or left border
  if (x < MIN_DISTANCE || y < MIN_DISTANCE) return false

  // too close to bottom or right border
  if (x > SVG_WIDTH - MIN_DISTANCE || y > SVG_HEIGHT - MIN_DISTANCE)
    return false

  // see if it's too close to any already-mapped points
  const tooClosePoint = find(points, function (point: Point) {
    return Math.hypot(point.x - x, point.y - y) < MIN_DISTANCE
  })
  if (tooClosePoint) {
    return false
  }

  // if none of the above are true, it's good to map!
  return true
}

const drawCornersAndEdges = () => {
  // add points at the corners and along the edges of the frame so the paths can't cross it
  const pointsPerEdge = Math.round(NUMBER_OF_EDGE_POINTS / 4)

  for (let i = 0; i < pointsPerEdge; i++) {
    // draw points along each side
    // top, from l to r
    points.push({
      i: -99,
      x: (SVG_WIDTH * i) / pointsPerEdge,
      y: 0,
      type: "wall"
    })
    // right, from t to b
    points.push({
      i: -99,
      x: SVG_WIDTH,
      y: (SVG_HEIGHT * i) / pointsPerEdge,
      type: "wall"
    })
    // bottom, from r to l
    points.push({
      i: -99,
      x: SVG_WIDTH - (SVG_WIDTH * i) / pointsPerEdge,
      y: SVG_HEIGHT,
      type: "wall"
    })
    // left, from b to t
    points.push({
      i: -99,
      x: 0,
      y: SVG_HEIGHT - (SVG_HEIGHT * i) / pointsPerEdge,
      type: "wall"
    })
  }

  if (SHOULD_DRAW_EXTRA_INFO) {
    points.forEach((point) => {
      if (point.i === -99) {
        svgElements += `<circle cx="${point.x}" cy="${point.y}" r="${POINT_RADIUS}" fill="${WALL_COLOR}" />`
      }
    })
  }
}

const drawPoint = (i: number) => {
  // recursive, if it's too close to another point
  const x = random(SVG_WIDTH)
  const y = random(SVG_HEIGHT)
  const isWall = Math.random() < WALL_PERCENT / 100

  if (checkIfDrawable(x, y)) {
    const color = isWall ? WALL_COLOR : PATH_COLOR
    points.push({ i, x, y, type: isWall ? "wall" : "path" })
    if (SHOULD_DRAW_EXTRA_INFO) {
      svgElements += `<circle cx="${x}" cy="${y}" r="${POINT_RADIUS}" fill="${color}" />`
    }
  } else {
    // prevent infinite loop if bad parameters are given
    if (failedPointDrawings < MAX_ALLOWABLE_FAILED_POINT_DRAWINGS) {
      // try again, recurse
      failedPointDrawings++
      drawPoint(i)
    } else {
      displayError()
      throw new Error(
        "Not all points were drawn! If settings are ok for drawing, consider increasing MAX_ALLOWABLE_FAILED_POINT_DRAWINGS."
      )
    }
  }
}

const drawPoints = () => {
  failedPointDrawings = 0
  for (let i = 0; i < NUMBER_OF_POINTS; i++) {
    drawPoint(i)
  }
}

const drawTriangleLineSegment = (i: number, p: Point, q: Point) => {
  // should only be called if SHOULD_DRAW_EXTRA_INFO
  svgElements += `<line x1="${p.x}" y1="${p.y}" x2="${q.x}" y2="${q.y}" stroke="black" opacity="0.2" />`
}

const drawTriangles = () => {
  const coords: number[] = flatMap(points, (point) => {
    return [point.x, point.y]
  })
  delaunay = new Delaunator(coords)
  if (SHOULD_DRAW_EXTRA_INFO) {
    forEachTriangleEdge(points, delaunay, drawTriangleLineSegment)
  }
}

const resetSvg = () => {
  lineSegments = []
  points = []
  shapes = []
  shapeTypes = []
  document.getElementById("svg").innerHTML = ""
  svgElements = `<rect width="100%" height="100%" fill="${
    SHOULD_DRAW_EXTRA_INFO ? "gray" : "black"
  }" />`
  document.getElementById("svg").setAttribute("width", SVG_WIDTH)
  document.getElementById("svg").setAttribute("height", SVG_HEIGHT)
  document
    .getElementById("svg")
    .setAttribute("viewBox", `0 0 ${SVG_WIDTH} ${SVG_HEIGHT}`)
  resetDownloadButton()
}

const drawCellSegment = (triangleIndex: number) => {
  if (!delaunay) return

  const p = getCentroid(getPointsOfTriangle(triangleIndex, points, delaunay))

  const oppositeHalfedge = delaunay.halfedges[triangleIndex]
  const q = getCentroid(getPointsOfTriangle(oppositeHalfedge, points, delaunay))

  lineSegments.push({
    x1: Math.round(p.x),
    y1: Math.round(p.y),
    x2: Math.round(q.x),
    y2: Math.round(q.y)
  })
  if (SHOULD_DRAW_EXTRA_INFO) {
    svgElements += `<line x1="${Math.round(p.x)}" y1="${Math.round(
      p.y
    )}" x2="${Math.round(q.x)}" y2="${Math.round(
      q.y
    )}" stroke="darkred" stroke-width="2" opacity="0.5" />`
  }
}

const investigateHalfedge = (triangleIndex: number, p: Point, q: Point) => {
  // if the points at the end of the edge are the same type, ignore them
  // if they're different types, draw a line segment between the centroids of the triangle on either side of the hedge
  if (p.type !== q.type) {
    drawCellSegment(triangleIndex)
  }
}

const checkIfShapeIsComplete = (
  shape: Shape,
  currentEndPoint: Point | undefined
) => {
  if (shape.length < 2) return
  if (!currentEndPoint) return

  return (
    first(shape)!.x === currentEndPoint.x &&
    first(shape)!.y === currentEndPoint.y
  )
}

const getMidpoint = (pointA: Point, pointB: Point): Point => {
  return {
    x: pointA.x + (pointB.x - pointA.x) / 2,
    y: pointA.y + (pointB.y - pointA.y) / 2
  }
}

const transformShapeToCurves = (shape: Shape): Curve[] => {
  // old points become control points
  let curves: Curve[] = []
  shape.forEach((point, i) => {
    if (i === shape.length - 1) return
    const pointAfter: Point = i === shape.length - 1 ? shape[0] : shape[i + 1]
    const midpoint = getMidpoint(point, pointAfter)
    curves.push({
      // x1 & y1 are original point (i.e. the red ones)
      // x & y are the midpoints (the green ones)
      x1: Math.round(point.x),
      y1: Math.round(point.y),
      x: Math.round(midpoint.x),
      y: Math.round(midpoint.y)
    })
  })
  return curves
}

const drawSmoothedShapes = () => {
  const curves = shapes.map((shape) => {
    return transformShapeToCurves(shape)
  })

  each(curves, (smoothedShape, j) => {
    const shapeType = shapeTypes[j]
    const fillColor = shapeType === "wall" ? "black" : "white"
    svgElements += `<path d="`
    each(smoothedShape, (point, i) => {
      if (i === 0) {
        const lastPoint: Curve = last(smoothedShape)!
        svgElements += `M ${lastPoint.x} ${lastPoint.y}`
        svgElements += `Q ${point.x1} ${point.y1}, ${point.x} ${point.y} `
      } else {
        svgElements += `T ${point.x} ${point.y} `
      }
    })
    svgElements += `" fill="${fillColor}" fill-opacity="1" />`
    if (SHOULD_DRAW_EXTRA_INFO) {
      each(smoothedShape, (point) => {
        svgElements += `<circle cx="${point.x1}"", cy="${point.y1}" r="1" fill="red" />`
        svgElements += `<circle cx="${point.x}"", cy="${point.y}" r="1" fill="green" />`
      })
    }
  })
}

const drawUnsmoothedShapes = () => {
  each(shapes, (shape) => {
    svgElements += `<path d="`
    each(shape, (point, i) => {
      if (i === 0) {
        svgElements += `M ${point.x} ${point.y} `
      } else {
        // TODO: if SHOULD_SMOOTH is true, smooth
        // prior to this, transform the shape to its midpoints
        svgElements += `L ${point.x} ${point.y} `
      }
    })
    svgElements += `" fill="white" />`
  })
}

const allEqual = (arr: Array<unknown>): boolean => {
  return arr.every((value) => value === arr[0])
}

const getTypeOfShape = (shape: Shape): string => {
  // find the types of the points inside the shape
  // if they're all wall type, the shape is a wall
  // otherwise it's a path
  // let's just ... assume nothing more complicated than this will happen, ok? ;)
  let pointTypesInShape: string[] = []

  const reformattedShape = shape.map((point) => {
    return [point.x, point.y]
  })

  points.forEach((point) => {
    const reformattedPoint = [point.x, point.y]

    if (pointInPolygon(reformattedPoint, reformattedShape)) {
      pointTypesInShape.push(point.type!)
    }
  })

  // check if all the points inside the shape are the same type
  if (allEqual(pointTypesInShape) && pointTypesInShape[0] === "wall") {
    return "wall"
  }

  return "path"
}

const drawShapes = () => {
  // figure out what type each shape is
  shapeTypes = shapes.map((shape: Shape) => {
    return getTypeOfShape(shape)
  })

  // if ALLOW_WALL_ISLANDS is false, delete all "wall" shapes
  if (!ALLOW_WALL_ISLANDS) {
    // loop backwards since we'll be deleting items from the array
    for (let i = shapeTypes.length - 1; i >= 0; i--) {
      const shapeType = shapeTypes[i]

      if (shapeType === "wall") {
        // delete the shape from both the shapes array and the shapeTypes array
        shapes.splice(i, 1)
        shapeTypes.splice(i, 1)
      }
    }
  }

  // draw the shapes
  if (SHOULD_SMOOTH) {
    drawSmoothedShapes()
  } else {
    drawUnsmoothedShapes()
  }
}

const startANewShape = (remainingSegments: LineSegment[]) => {
  const segment = remainingSegments[0]

  shapes.push([{ x: segment.x1, y: segment.y1 }])
  const endPoint = { x: segment.x2, y: segment.y2 }
  remainingSegments.shift()

  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  findAndHandleNextLineSegment(remainingSegments, endPoint)
}

const findAndHandleNextLineSegment = (
  remainingSegments: LineSegment[],
  endPoint: Point
) => {
  // recursive
  let currentEndPoint: Point = endPoint

  if (!currentEndPoint) {
    displayError()
    throw new Error("no currentEndPoint")
  }

  // see if a line segment begins where the current shape ends
  const lineSegmentStartingAtPoint = findIndex(remainingSegments, (segment) => {
    return segment.x1 === currentEndPoint.x && segment.y1 === currentEndPoint.y
  })

  // see if a line segment ends where the current shape ends
  const lineSegmentEndingAtPoint = findIndex(remainingSegments, (segment) => {
    return segment.x2 === currentEndPoint.x && segment.y2 === currentEndPoint.y
  })

  if (lineSegmentStartingAtPoint > -1) {
    // add the line segment to the shape
    const segment = remainingSegments[lineSegmentStartingAtPoint]
    last(shapes)!.push({
      x: segment.x1,
      y: segment.y1
    })
    currentEndPoint = {
      x: segment.x2,
      y: segment.y2
    }
    remainingSegments.splice(lineSegmentStartingAtPoint, 1)
  } else if (lineSegmentEndingAtPoint > -1) {
    // reverse the line segment and add it to the shape
    const segment = remainingSegments[lineSegmentEndingAtPoint]
    last(shapes)!.push({
      x: segment.x2,
      y: segment.y2
    })
    currentEndPoint = {
      x: segment.x1,
      y: segment.y1
    }
    remainingSegments.splice(lineSegmentEndingAtPoint, 1)
  } else {
    displayError()
    throw new Error("no line segment found")
  }

  // check if shape is complete
  if (checkIfShapeIsComplete(last(shapes)!, currentEndPoint)) {
    // add first point to end of shape
    last(shapes)!.push(currentEndPoint)
    if (remainingSegments.length > 0) {
      startANewShape(remainingSegments)
    } else {
      // done finding all shapes!
      drawShapes()
    }
  } else {
    findAndHandleNextLineSegment(remainingSegments, currentEndPoint)
  }
}

const convertSegmentsToShapes = () => {
  // start with any line segment
  // from the end of that line segment, find the segment that has the end as one of its points
  // connect those two segments
  // repeat

  // once it's looped back and the end is the beginning, start a new shape
  const remainingSegments: LineSegment[] = cloneDeep(lineSegments)

  startANewShape(remainingSegments)
}

const drawCells = () => {
  // loop over all the half-edges
  // if the half-edge is between a wall & path, find the triangle of it and the other, same half-edge and draw a line between their centroids
  // mark those half-edges as complete
  // if it's between two walls or two paths, mark it as complete without drawing a line
  if (!delaunay) return

  forEachTriangleEdge(points, delaunay, investigateHalfedge)
  convertSegmentsToShapes()
}

export const drawCaves = () => {
  resetSvg()
  drawCornersAndEdges()
  drawPoints()
  drawTriangles()
  drawCells()
  // TODO: only need to draw shapes that are in between a white node & a black node
  // essentially, only draw lines around the walls
  // for sure, any edges along triangles where all three points are the same color can be removed

  document.getElementById("svg").innerHTML = svgElements
  document.getElementById("data").innerHTML =
    `<svg viewBox="0 0 ${SVG_WIDTH} ${SVG_HEIGHT}" xmlns="http://www.w3.org/2000/svg">` +
    svgElements +
    `</svg>`
  updateDownloadButton()
}
